import { Link } from "react-router-dom";
import Header from "../Header";
import { Icon } from "../Icon";
import { PlaceMark } from "../trip/TripFormAtoms";
import { usePlatformConfig } from "../../utils/platformConfig";
import { splitValues } from "../../utils/listChoices";

/* The home lists on a laptop (Oct 2026, mock Laptop_1). A phone never sees any of this:
   every piece is hidden below the lg breakpoint, and the phone layout is hidden above
   it where the two differ. */

/* The top bar on the pages that have their own phone header - the trip forms, a car's
   page, a driver's page, /trips (mock Laptop_2, change 1). Only a laptop sees it; with it
   on the page the phone's tab bar steps aside there too. */
export function LaptopTopBar({ subtitle }) {
  let city = "";
  try { city = localStorage.getItem("sramika_selected_city") || ""; } catch { /* storage off */ }
  return <div className="hm hidden lg:block"><Header city={city} subtitle={subtitle} /></div>;
}

/* Three short promises under the search bar (mock Laptop_2, change 10) - what is true of
   every listing, never anything about logging in. */
export function TrustRow({ items }) {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5">
      {items.map(([icon, word]) => (
        <span key={word} className="flex items-center gap-2 text-[14px] font-bold text-[#334155]">
          <span className="w-7 h-7 rounded-full bg-white border border-[#DCEBB8] flex items-center justify-center"><Icon name={icon} size={14} stroke="#5E8700" strokeWidth={2.3} /></span>
          {word}
        </span>
      ))}
    </div>
  );
}

/** A headline, a line under it, the trip as one wide search bar - and a card beside it. */
export function DesktopHero({ title, sub, search, aside, below }) {
  return (
    <section className="hidden lg:flex items-start gap-7 mt-6 rounded-[22px] border border-[#E3EED0] px-8 py-7"
      style={{ background: "linear-gradient(120deg,#F4F9E8 0%,#FFFFFF 55%,#EEF6DD 100%)" }}>
      <div className="flex-1 min-w-0">
        <h1 className="text-[30px] font-extrabold text-[#0F172A] leading-tight tracking-[-0.3px]">{title}</h1>
        <p className="text-[15px] text-[#475569] mt-1.5 mb-5">{sub}</p>
        {search}
        {below}
      </div>
      {aside && <div className="w-[340px] shrink-0">{aside}</div>}
    </section>
  );
}

/* The trip in one bar: Pickup · Drop · When · Trip, then the button. Each part opens the
   same trip form the phone uses. */
export function DesktopSearchBar({ parts, cta, onOpen, onCta }) {
  return (
    <div className="flex items-stretch h-[70px] bg-white border border-[#E2E8F0] rounded-2xl shadow-[0_8px_24px_rgba(15,23,42,0.07)] overflow-hidden">
      {parts.map((p, i) => (
        <button key={i} type="button" onClick={onOpen}
          className={`${p.narrow ? "w-[150px] shrink-0" : "flex-1 min-w-0"} text-left px-4 border-r border-[#EEF2F7] hover:bg-[#F8FBF1] transition-colors`}>
          <span className="flex items-center gap-1.5 text-[12px] font-semibold text-[#64748B]">
            {p.kind ? <PlaceMark kind={p.kind} size={18} /> : <span className="text-[#5E8700]">{p.icon}</span>}
            {p.label}
          </span>
          <span className={`block mt-1 text-[14.5px] font-bold truncate ${p.value ? "text-[#0F172A]" : "text-[#94A3B8]"}`}>{p.value || p.placeholder}</span>
        </button>
      ))}
      <button type="button" onClick={onCta}
        className="w-[160px] shrink-0 bg-[#7BAE00] hover:bg-[#6E9D00] text-white text-[15px] font-extrabold transition-colors">
        {cta}
      </button>
    </div>
  );
}

/* The filters, always open beside the list: one choice for the order, many for the rest -
   stored as the chips and the phone's Filters sheet store them (comma lists). */
export function FilterPanel({ title, sort, groups, values, onToggle, onClear, clearLabel }) {
  const OPT = (on) => `min-h-[32px] px-3 rounded-full border text-[12.5px] font-semibold transition-colors ${
    on ? "bg-[#F1F7E3] border-[#7BAE00] text-[#3F5E00]" : "bg-white border-[#E2E8F0] text-[#374151] hover:border-[#B9D77A]"}`;
  const any = Object.values(values || {}).some(Boolean);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-4 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
      <div className="flex items-center justify-between">
        <p className="text-[15px] font-extrabold text-[#0F172A]">{title}</p>
        {any && <button type="button" onClick={onClear} className="text-[12.5px] font-bold text-[#3F5E00] hover:underline">{clearLabel}</button>}
      </div>
      {sort && (
        <div className="mt-3">
          <p className="text-[12.5px] font-bold text-[#334155] mb-1.5">{sort.label}</p>
          <div className="flex flex-wrap gap-1.5">
            {sort.options.map((o) => <button key={o.value} type="button" onClick={() => sort.onChange(o.value)} className={OPT(sort.value === o.value)}>{o.label}</button>)}
          </div>
        </div>
      )}
      {groups.flatMap((g) => g.rows).map((r) => (
        <div key={r.key} className="mt-3.5">
          <p className="text-[12.5px] font-bold text-[#334155] mb-1.5">{r.label}</p>
          {r.note && <p className="text-[11px] text-[#B45309] mb-1.5 leading-snug">{r.note}</p>}
          <div className="flex flex-wrap gap-1.5">
            {r.options.map((o) => {
              const on = splitValues(values?.[r.key]).includes(o.value);
              return <button key={o.value} type="button" disabled={r.disabled} aria-pressed={on} onClick={() => onToggle(r.key, o.value)} className={`${OPT(on)} disabled:opacity-40`}>{o.label}</button>;
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/** A comma list with one value switched on or off (a typed-in old range is dropped). */
export const toggled = (list, value) => {
  const now = splitValues(list).filter((v) => !v.startsWith("CUSTOM:"));
  return (now.includes(value) ? now.filter((v) => v !== value) : [...now, value]).join(",");
};

/* The footer: what Sramika is, how to reach it, joining, and the policies. */
export function DesktopFooter({ t }) {
  const cfg = usePlatformConfig();
  const digits = String(cfg?.supportPhoneNumber || "").replace(/\D/g, "").slice(-10);
  const phone = digits.length === 10 && digits !== "9999999999" ? `+91 ${digits.slice(0, 5)} ${digits.slice(5)}` : null;
  const email = String(cfg?.supportEmail || "").trim();
  const L = ({ to, children }) => <Link to={to} className="block text-[13px] text-[#CBD5E1] hover:text-white mt-1.5">{children}</Link>;
  return (
    <footer className="bottom-footer hidden lg:block bg-[#0F172A] mt-10">
      <div className="max-w-[1240px] mx-auto px-6 py-9 grid grid-cols-5 gap-10">
        <div>
          <p className="text-[18px] font-extrabold text-[#9BCB2E]">{t("lg_brand")}</p>
          <p className="text-[13px] text-[#CBD5E1] mt-1.5 leading-relaxed">{t("dk_tagline")}</p>
          {phone && <a href={`tel:+91${digits}`} className="flex items-center gap-1.5 text-[13px] text-white mt-3"><Icon name="phone" size={14} stroke="#9BCB2E" strokeWidth={2.2} />{phone}</a>}
          {email && <a href={`mailto:${email}`} className="block text-[13px] text-[#CBD5E1] mt-1.5">{email}</a>}
        </div>
        <div><p className="text-[13.5px] font-bold text-white">{t("dk_fCustomers")}</p><L to="/">{t("navCars")}</L><L to="/drivers">{t("navHireDriver")}</L><L to="/trips">{t("dk_trips")}</L><L to="/bookings">{t("bookings")}</L></div>
        {/* By city (mock Seo_3): the pages Google lists for "cabs in <city>". */}
        <div><p className="text-[13.5px] font-bold text-white">{t("dk_fByCity")}</p><L to="/cabs">{t("dk_fCabsByCity")}</L><L to="/hire-drivers">{t("dk_fDriversByCity")}</L><L to="/taxi-fare/vijayawada">{t("dk_fFareChart")}</L><L to="/airport-taxi/vijayawada">{t("dk_fAirport")}</L></div>
        <div><p className="text-[13.5px] font-bold text-white">{t("dk_fJoin")}</p><L to="/cab-owner/onboarding">{t("dk_fRegisterCar")}</L><L to="/alting-driver/onboarding">{t("dk_fJoinDriver")}</L></div>
        <div><p className="text-[13.5px] font-bold text-white">{t("dk_fAbout")}</p><L to="/terms">{t("dk_fTerms")}</L><L to="/privacy">{t("dk_fPrivacy")}</L><L to="/refund-policy">{t("dk_fRefunds")}</L><L to="/delete-account">{t("dk_fDelete")}</L></div>
      </div>
    </footer>
  );
}

/* Log in on a laptop (mock Laptop_3, changes 1-2): Sramika and three promises on a
   coloured half, the phone's own log-in card on the other at a laptop's size. A phone
   sees only the card, exactly as before. */
export function AuthSplit({ dark = false, title, sub, points, extra, children }) {
  return (
    <div className="w-full lg:flex lg:min-h-screen">
      <aside className={`hidden lg:flex lg:w-[46%] flex-col justify-center px-[6vw] text-white ${dark ? "bg-[linear-gradient(140deg,#1E293B,#0F172A)]" : "bg-[linear-gradient(140deg,#7BAE00,#4F7A00)]"}`}>
        <img src="/logo-160.png" alt="" className="w-16 h-16 rounded-full bg-white object-contain p-1" />
        <p className="mt-6 text-[36px] font-extrabold leading-tight tracking-[-0.3px]">{title}</p>
        <p className="mt-2 text-[16px] text-white/85 max-w-[420px] leading-snug">{sub}</p>
        <ul className="mt-8 space-y-4">
          {points.map(([icon, word]) => (
            <li key={word} className="flex items-center gap-3 text-[16px] font-bold">
              <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"><Icon name={icon} size={17} stroke="#FFFFFF" strokeWidth={2.2} /></span>
              {word}
            </li>
          ))}
        </ul>
        {extra}
      </aside>
      <div className="lg:flex-1 lg:flex lg:items-center lg:justify-center lg:py-10">
        <div className="lg:w-[400px] lg:[zoom:1.25]">{children}</div>
      </div>
    </div>
  );
}

/* Terms, Privacy, Refunds, Delete account on a laptop (mock Laptop_3, change 5): the top
   bar, a list of the sections on the left (each a link down the page), the text at a
   reading width, the footer. Sections carry ids "sec-0", "sec-1", ... */
export function DocFrame({ t, contents, title, children }) {
  return (
    <>
      <LaptopTopBar />
      <div className="lg:max-w-[1100px] lg:mx-auto lg:px-6 lg:pt-6 lg:flex lg:gap-8 lg:items-start">
        {contents?.length > 0 && (
          <nav className="hidden lg:block w-[260px] shrink-0 sticky top-[84px] bg-white border border-[#E5E7EB] rounded-2xl p-4 max-h-[calc(100vh-110px)] overflow-y-auto">
            {title && <p className="text-[14px] font-extrabold text-[#0F172A] mb-2">{title}</p>}
            {contents.map((c, i) => (
              <a key={c} href={`#sec-${i}`} className="block py-1.5 text-[13px] text-[#334155] hover:text-[#3F5E00] leading-snug">{c}</a>
            ))}
          </nav>
        )}
        <div className="hm lg:flex-1 lg:min-w-0 lg:[&_.page-center-md]:px-0">{children}</div>
      </div>
      <DesktopFooter t={t} />
    </>
  );
}
